/* global axios */
import ApiClient from './ApiClient';

class FunnelsAPI extends ApiClient {
  constructor() {
    super('funnels', { accountScoped: true });
  }

  // `archived: true` returns the funnel's archived cards instead of the active
  // ones — never both, so the board and the archived view stay disjoint.
  getCards(funnelId, { archived } = {}) {
    return axios.get(`${this.url}/${funnelId}/funnel_cards`, {
      params: archived ? { archived: true } : {},
    });
  }

  createCard(funnelId, { funnelStageId, conversationId, customFieldValues }) {
    return axios.post(`${this.url}/${funnelId}/funnel_cards`, {
      funnel_stage_id: funnelStageId,
      conversation_id: conversationId,
      custom_field_values: customFieldValues,
    });
  }

  // Persists the card's custom field values. The backend runs the required-field
  // validation and answers with a structured 422 ({ errors: [{ key, name, code }] })
  // that the UI turns into per-field messages.
  updateCard(funnelId, cardId, { customFieldValues }) {
    return axios.patch(`${this.url}/${funnelId}/funnel_cards/${cardId}`, {
      custom_field_values: customFieldValues,
    });
  }

  moveCard(funnelId, cardId, { funnelStageId, position }) {
    return axios.patch(`${this.url}/${funnelId}/funnel_cards/${cardId}/move`, {
      funnel_stage_id: funnelStageId,
      position,
    });
  }

  // Takes the card off the board without deleting it. The card keeps holding its
  // conversation's slot in the funnel, so a later create for that conversation is
  // rejected with a `card_archived` 422 instead of silently recreating it.
  archiveCard(funnelId, cardId) {
    return axios.post(`${this.url}/${funnelId}/funnel_cards/${cardId}/archive`);
  }

  unarchiveCard(funnelId, cardId) {
    return axios.post(
      `${this.url}/${funnelId}/funnel_cards/${cardId}/unarchive`
    );
  }

  reorderStages(funnelId, stageIds) {
    return axios.patch(`${this.url}/${funnelId}/funnel_stages/reorder`, {
      stage_ids: stageIds,
    });
  }

  // Admin CRUD (Fatia 7). Payloads are nested under the resource key so Rails
  // params.require works and non-column keys like team_ids survive.
  createFunnel(payload) {
    return axios.post(this.url, { funnel: payload });
  }

  updateFunnel(id, payload) {
    return axios.patch(`${this.url}/${id}`, { funnel: payload });
  }

  deleteFunnel(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  createStage(funnelId, payload) {
    return axios.post(`${this.url}/${funnelId}/funnel_stages`, {
      funnel_stage: payload,
    });
  }

  updateStage(funnelId, stageId, payload) {
    return axios.patch(`${this.url}/${funnelId}/funnel_stages/${stageId}`, {
      funnel_stage: payload,
    });
  }

  deleteStage(funnelId, stageId) {
    return axios.delete(`${this.url}/${funnelId}/funnel_stages/${stageId}`);
  }

  // Custom fields admin (Fatia 9b).
  createField(funnelId, payload) {
    return axios.post(`${this.url}/${funnelId}/funnel_fields`, {
      funnel_field: payload,
    });
  }

  updateField(funnelId, fieldId, payload) {
    return axios.patch(`${this.url}/${funnelId}/funnel_fields/${fieldId}`, {
      funnel_field: payload,
    });
  }

  deleteField(funnelId, fieldId) {
    return axios.delete(`${this.url}/${funnelId}/funnel_fields/${fieldId}`);
  }

  reorderFields(funnelId, fieldIds) {
    return axios.patch(`${this.url}/${funnelId}/funnel_fields/reorder`, {
      field_ids: fieldIds,
    });
  }
}

export default new FunnelsAPI();
